/**
 * Dashboard (Screen A, landing "/") — the PACE care-team lead's COCKPIT (UI Wave-2 A8/A10). It leads
 * with the four DECISION metrics a lead actually acts on — open care gaps, members with high chronic
 * burden, immunization coverage, and the roster / PACE-cohort diagnostic — NOT raw row counts. Every
 * number is sourced from a REAL gold/operational service (GET /api/v1/dashboard/decision-metrics) and
 * badged from THAT metric's server-derived provenance via ProvenanceBadge (never hand-tagged).
 *
 * Honesty contract (unchanged from the row-count era, now per-metric): each tile's BODY is gated on its
 * derived tier — REAL/PARTIAL show the metric; DEFERRED/MOCK show "Awaiting feed" (the dangerous-zero
 * guard), NEVER a fabricated 0. COUNT-vs-RATE: the immunization tile shows a % ONLY when a real
 * precomputed denominator exists, else an honest measure count. DIRECTOR H-2: there is NO due-date
 * source, so there is NO "% overdue" tile. The non-cohort caveat is ALWAYS-VISIBLE on every tile whose
 * denominator is the non-PACE-validated sandbox roster. Each tile drills through to its detail screen.
 */
import type { ComponentType, ReactNode } from "react";
import {
  SkyCard,
  SkyLink,
  SkyText,
  SkyLoader,
  SkyMessageBar,
} from "@skypointcloud/skypoint-ui";
import { AlertOctagon, Activity, Syringe, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { DashboardDecisionMetrics, Provenance } from "@shared/contracts";
import { usePage } from "../lib/usePage";
import { COPY } from "../lib/copy";
import { deriveBadge } from "../provenance/deriveBadge";
import { ProvenanceBadge } from "../provenance/ProvenanceBadge";
import { PageError } from "../components/PageError";
import { PageHeader } from "../shell/PageHeader";
import { PageFooter } from "../shell/PageFooter";

// P1-2: the structural non-cohort caveat is stated ONCE per screen (a single page-level banner below),
// NOT repeated on every tile - the REAL* badge on each tile carries the per-tile reminder. It is a
// STRUCTURAL/informational caveat (the denominator is unproven), distinct from the elevated missing-data
// safety banner. op_dim_member (2299) is a GENERAL FHIR population, NOT the PACE-enrolled cohort
// (0 of 2299 coverage-confirmed); rates here are structurally real on an unproven denominator.
const NON_COHORT_CAVEAT =
  "Denominator unproven: this sandbox is a general FHIR population (2299), not the PACE-enrolled cohort " +
  "(0 of 2299 PACE-confirmed). Re-validate on a real ElementCare extract before operational use.";

// Leadership-facing scope boundary: these tiles are CLINICAL care-gap indicators, NOT the CMS/HPMS PACE
// quality reporting program. Stated once, plainly, so no one infers regulatory coverage from this cockpit.
const CMS_SCOPE_NOTE =
  "These are clinical care-gap indicators, not the CMS/HPMS-required PACE quality reporting program " +
  "(NPA / DataPACE3 plus HPMS Level I and II, IDT per 42 CFR 460.102, enrollment and mortality, " +
  "grievances and appeals), which is out of scope and unbuilt.";

/**
 * A decision tile (skySign stat-card parity): icon + server-derived ProvenanceBadge + a tier-gated big
 * number + caption, with an optional always-visible non-cohort caveat and an optional drill-through link.
 * The body is gated on the derived tier so a DEFERRED/MOCK metric reads "Awaiting feed", never a
 * fabricated zero. NO number renders without its provenance badge.
 */
function DecisionTile({
  icon: Icon,
  title,
  provenance,
  ready,
  error,
  onRetry,
  render,
  drill,
}: {
  icon: ComponentType<{ size?: number; className?: string }>;
  title: string;
  provenance: Provenance | undefined;
  ready: boolean; // page-level: the aggregate payload has loaded and this metric is present
  error: string | null;
  onRetry: () => void;
  render: () => ReactNode;
  drill?: { label: string; to: string };
}) {
  const navigate = useNavigate();
  const tier = deriveBadge(provenance).tier;
  const showMetric = tier === "REAL" || tier === "PARTIAL";

  let body: ReactNode;
  if (error) {
    // Fail-forward: a transient page-load failure offers a Try-again that re-fires the whole
    // decision-metrics fetch, so the tile is never a dead end waiting on an F5.
    body = <PageError title="Something went wrong" onRetry={onRetry} />;
  } else if (!ready) {
    body = <SkyLoader size="small" label="Loading" />;
  } else if (!showMetric) {
    body = <SkyText size={300}>{COPY.awaitingFeedHolding}</SkyText>;
  } else {
    body = render();
  }

  return (
    <SkyCard>
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon size={20} className="text-neutral-500" />
            <SkyText size={300} weight="semibold">
              {title}
            </SkyText>
          </div>
          {/* P1-7: showNote surfaces the DEFERRED/MOCK feed as a wrapping caption under the chip, so the
              chip label stays the short constant "DEFERRED" and the feed name never clips this tile header. */}
          <ProvenanceBadge meta={provenance} showNote />
        </div>
        {body}
        {drill && (
          <SkyLink
            href={drill.to}
            onClick={(e) => {
              e.preventDefault();
              navigate(drill.to);
            }}
          >
            {drill.label}
          </SkyLink>
        )}
      </div>
    </SkyCard>
  );
}

/** A big single value with a caption, for the REAL/PARTIAL tiles. */
function Metric({ value, caption }: { value: string; caption: string }) {
  return (
    <div className="flex flex-col">
      <SkyText size={700} weight="bold">
        {value}
      </SkyText>
      <SkyText size={200}>{caption}</SkyText>
    </div>
  );
}

export function Dashboard() {
  const navigate = useNavigate();
  const { data, loading, error, retry } = usePage<DashboardDecisionMetrics>(
    "/api/v1/dashboard/decision-metrics",
  );
  const ready = !loading && data != null;

  return (
    <>
      <PageHeader
        crumbs={[{ key: "home", label: "skyCare" }, { key: "dashboard", label: "Dashboard" }]}
        title="Dashboard"
        description="The care-team lead's cockpit: open care gaps, high chronic burden, immunization coverage, and the PACE-cohort diagnostic, each sourced from real data and badged with where it came from."
      />
      <section className="mt-6 flex flex-1 flex-col gap-4">
        {/* P1-2: the structural non-cohort caveat is stated ONCE here (informational), not on every tile.
            It is calmer (info) than the elevated missing-data banner so safety-critical alerts keep salience. */}
        <SkyMessageBar intent="info" title="Denominator unproven" message={NON_COHORT_CAVEAT} />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {/* 1) Open care gaps — REAL/gold (gold.fact_measure_score). 152 open across 83 members
              (distinct member x measure after rate-id de-dup). */}
          <DecisionTile
            icon={AlertOctagon}
            title="Open care gaps"
            provenance={data?.openCareGaps.provenance}
            ready={ready}
            error={error}
            onRetry={retry}
            drill={{ label: "Open the care-gap worklist", to: "/care-gaps" }}
            render={() => (
              <Metric
                value={String(data?.openCareGaps.open ?? 0)}
                caption={`open gaps across ${data?.openCareGaps.members ?? 0} members`}
              />
            )}
          />

          {/* 2) High chronic burden — op_dim_member.chronic_condition_count >= 3. 30 members. */}
          <DecisionTile
            icon={Activity}
            title="High chronic burden"
            provenance={data?.chronicBurden.provenance}
            ready={ready}
            error={error}
            onRetry={retry}
            drill={{ label: "Open the chronic-burden worklist", to: "/population" }}
            render={() => (
              <Metric
                value={String(data?.chronicBurden.count ?? 0)}
                caption={`members with >= ${data?.chronicBurden.threshold ?? 3} chronic conditions`}
              />
            )}
          />

          {/* 3) Immunization coverage — COUNT-vs-RATE honesty: a % only with a real precomputed
              denominator, else an honest measure count. The rate feed is DEFERRED in this sandbox. */}
          <DecisionTile
            icon={Syringe}
            title="Immunization coverage"
            provenance={data?.immunization.provenance}
            ready={ready}
            error={error}
            onRetry={retry}
            render={() =>
              data?.immunization.rate != null ? (
                <Metric
                  value={`${Math.round(data.immunization.rate)}%`}
                  caption="mean precomputed immunization coverage"
                />
              ) : (
                <Metric
                  value={String(data?.immunization.measuresTracked ?? 0)}
                  caption="immunization measures tracked (coverage rate pending a real denominator)"
                />
              )
            }
          />

          {/* 4) Roster / PACE-cohort — the "0 of 2299 PACE-confirmed" mini-diagnostic (coverage.service). */}
          <DecisionTile
            icon={Users}
            title="Roster / PACE-cohort"
            provenance={data?.cohort.provenance}
            ready={ready}
            error={error}
            onRetry={retry}
            drill={{ label: "Open the census cohort diagnostic", to: "/census" }}
            render={() => (
              <div className="flex flex-col gap-1">
                <Metric
                  value={String(data?.cohort.roster_total ?? 0)}
                  caption="members in the operation roster"
                />
                <SkyText size={200} weight="medium">
                  {`${data?.cohort.pace_confirmed_members ?? 0} of ${data?.cohort.roster_total ?? 0} PACE-confirmed`}
                </SkyText>
              </div>
            )}
          />
        </div>

        {/* CMS scope boundary (B5): clinical indicators are NOT the regulatory PACE quality program. */}
        <SkyMessageBar
          intent="info"
          title="Not the CMS PACE quality reporting program"
          message={CMS_SCOPE_NOTE}
        />

        {/* Provenance meta-control: the Real-vs-Mock matrix (a navigation card, NOT a fabricated metric). */}
        <SkyCard header={{ title: "Real-vs-Mock matrix", description: "The provenance contract" }}>
          <div className="flex flex-col gap-2">
            <SkyText size={300}>
              Every tile by authoritative status, backing model, and unblocking feed: the meta-control
              that makes each badge above trustworthy.
            </SkyText>
            <SkyLink
              href="/data-health/matrix"
              onClick={(e) => {
                e.preventDefault();
                navigate("/data-health/matrix");
              }}
            >
              Open the Real-vs-Mock matrix
            </SkyLink>
          </div>
        </SkyCard>
      </section>
      <PageFooter />
    </>
  );
}
